import addRemoveArrayQuery from "./addRemoveArrayQuery";
import getModifiedKeys from "./getModifiedKeys";
import {
  ClassListType,
  ContactListType,
  Course,
  LessonListType,
  Module,
  ParentListType,
  Program,
  Project,
  StudentListType,
  TeacherListType,
} from "./interfaces";

/**
 * Function used to format the class data in case we are updating.
 * Otherwise, just return the provided data.
 * @param selectedData
 * @param dataToProvide
 * @returns
 */
export const formatUpdateClass = (
  selectedData: ClassListType | undefined,
  dataToProvide: {
    name: string;
    teachers: string[];
    active?: boolean;
  },
) => {
  // If we are not editing, just return the provided data
  if (!selectedData) return dataToProvide;

  // Get just the ids of the teachers from the selectedData
  const { _id, teachers, ...selectedDataPlain } = selectedData;
  // Add just the ids of the teachers
  (selectedDataPlain as typeof dataToProvide).teachers = teachers.map(
    (contact) => contact._id,
  );

  // Return the states that have changed
  const toUpdate = getModifiedKeys(dataToProvide, selectedDataPlain);

  // Add added and removed
  toUpdate.added = { teachers: [] };
  toUpdate.removed = { teachers: [] };

  // Check if "teachers" has changed
  if (toUpdate.teachers) {
    // Append the information of what to add
    const { added, removed } = addRemoveArrayQuery(
      (selectedDataPlain as typeof dataToProvide).teachers,
      toUpdate.teachers,
    );

    // Adding the contacts to each list
    toUpdate.added.teachers = added;
    toUpdate.removed.teachers = removed;

    // Remove the plain teachers array from toUpdate
    delete toUpdate.teachers;
  }

  return toUpdate;
};

/**
 * Function used to format the student data in case we are updating.
 * Otherwise, just return the provided data.
 * @param selectedData
 * @param dataToProvide
 * @returns
 */
export const formatUpdateStudent = (
  selectedData: StudentListType | undefined,
  dataToProvide: {
    firstName: string;
    middleName: string;
    lastName: string;
    dateOfBirth: string;
    gender: string;
    classes: string[];
    parents: string[];
    heritage: boolean;
    notes: string[];
    role: string;
  },
) => {
  // If we are not editing, just return the provided data
  if (!selectedData) return dataToProvide;

  // Get just the ids from the classes and parents arrays from the selectedData
  const { _id, classes, parents, ...selectedDataPlain } = selectedData;

  // Check if the selected data has classes
  if (classes) {
    // Add just the ids of the classes
    (selectedDataPlain as typeof dataToProvide).classes = classes.map(
      (classItem) => classItem._id,
    );
  }

  if (parents) {
    // Add just the ids of the parents
    (selectedDataPlain as typeof dataToProvide).parents = parents.map(
      (parent) => parent._id,
    );
  }
  // Remove the role from the dataToProvide
  const { role, ...dataToCheck } = dataToProvide;

  // Return the states that have changed
  const toUpdate = getModifiedKeys(dataToCheck, selectedDataPlain);

  // Add added and removed
  toUpdate.added = { classes: [], parents: [] };
  toUpdate.removed = { classes: [], parents: [] };

  // Check if "classes" has changed
  if (toUpdate.classes) {
    // Append the information of what to add
    const { added, removed } = addRemoveArrayQuery(
      (selectedDataPlain as typeof dataToCheck).classes,
      toUpdate.classes,
    );

    // Adding the contacts to each list
    toUpdate.added.classes = added;
    toUpdate.removed.classes = removed;
  }

  // Check if "parents" has changed
  if (toUpdate.parents) {
    // Append the information of what to add
    const { added, removed } = addRemoveArrayQuery(
      (selectedDataPlain as typeof dataToCheck).parents,
      toUpdate.parents,
    );

    // Adding the contacts to each list
    toUpdate.added.parents = added;
    toUpdate.removed.parents = removed;
  }

  return toUpdate;
};

/**
 * Function used to format the student data in case we are updating.
 * Otherwise, just return the provided data.
 * @param selectedData
 * @param dataToProvide
 * @returns
 */
export const formatUpdateContact = (
  selectedData:
    | ContactListType
    | TeacherListType
    | ParentListType
    | undefined
    | null,
  dataToProvide: {
    firstName: string;
    middleName: string;
    lastName: string;
    email: string;
    phone: string;
    // role: string;
    // addAsPrimaryContact: boolean;
    organisations?: string[];
  },
) => {
  // If we are not editing, just return the provided data
  if (!selectedData) return dataToProvide;

  // Get just the ids from the classes and parents arrays from the selectedData
  const { _id, ...selectedDataPlain } = selectedData;

  // Remove the role from the dataToProvide
  const {
    //role, addAsPrimaryContact,
    organisations,
    ...dataToCheck
  } = dataToProvide;

  // Return the states that have changed
  const toUpdate = getModifiedKeys(dataToCheck, selectedDataPlain);

  return toUpdate;
};

/**
 * Function used to format the lesson data in case we are updating.
 * Otherwise, just return the provided data.
 * @param selectedData
 * @param dataToProvide
 * @returns
 */
export const formatUpdateLesson = (
  selectedData: Program | Course | Project | Module | null | undefined,
  dataToProvide: LessonListType,
) => {
  // If we are not editing, just return the provided data
  if (!selectedData) {
    // Get the files
    const {
      headerImage,
      badgeImage,
      planDocument,
      initialCode,
      finalCode,
      ...rest
    } = dataToProvide;

    // Define an output
    const dataToProvideOut = { ...rest } as { [key: string]: any };

    // If files are defined, then send the create signal
    if (planDocument) dataToProvideOut.planDocumentSignal = "create";
    if (headerImage) dataToProvideOut.headerImageSignal = "create";
    if (badgeImage) dataToProvideOut.badgeImageSignal = "create";
    if (initialCode) dataToProvideOut.initialCodeSignal = "create";
    if (finalCode) dataToProvideOut.finalCodeSignal = "create";

    return dataToProvideOut;
  }

  // Get just the ids of the lessons from the selectedData
  const { _id, ...selectedDataPlain } = selectedData;

  // Check if the selectedData has quizzes
  if ((selectedDataPlain as Course).quizzes) {
    // Get the quizzes
    const quizzes = (selectedDataPlain as Course).quizzes;

    // Add just the ids of the quizzes
    (selectedDataPlain as typeof dataToProvide).quizzes = quizzes.map((quiz) =>
      typeof quiz === "object" ? quiz._id : quiz,
    );
  }

  // Return the states that have changed
  const toUpdateRaw = getModifiedKeys(dataToProvide, selectedDataPlain);

  // Separate files from the rest of the modified keys
  const {
    headerImage,
    badgeImage,
    planDocument,
    initialCode,
    finalCode,
    ...toUpdate
  } = toUpdateRaw;

  // Check if files are null or undefined, and do not include them in the update data
  if (planDocument) {
    toUpdate.planDocumentSignal = "update";
    // Ensure the name is included even if unchanged
    if (
      !toUpdate.planName &&
      (selectedDataPlain as Project | Module).planName
    ) {
      toUpdate.planName = (selectedDataPlain as Project | Module).planName;
    }
  } else if (planDocument === "") toUpdate.planDocumentSignal = "delete";

  if (headerImage) toUpdate.headerImageSignal = "update";
  else if (headerImage === "") toUpdate.headerImageSignal = "delete";

  if (badgeImage) toUpdate.badgeImageSignal = "update";
  else if (badgeImage === "") toUpdate.badgeImageSignal = "delete";

  if (toUpdate.initialCodeName || initialCode) {
    toUpdate.initialCodeSignal = "update";
    // Ensure the name is included even if unchanged
    if (
      !toUpdate.initialCodeName &&
      (selectedDataPlain as Project | Module).initialCodeName
    ) {
      toUpdate.initialCodeName = (
        selectedDataPlain as Project | Module
      ).initialCodeName;
    }
  } else if (toUpdate.initialCodeName === "")
    toUpdate.initialCodeSignal = "delete";

  if (toUpdate.finalCodeName || finalCode) {
    toUpdate.finalCodeSignal = "update";
    // Ensure the name is included even if unchanged
    if (
      !toUpdate.finalCodeName &&
      (selectedDataPlain as Project | Module).finalCodeName
    ) {
      toUpdate.finalCodeName = (
        selectedDataPlain as Project | Module
      ).finalCodeName;
    }
  } else if (toUpdate.finalCodeName === "") toUpdate.finalCodeSignal = "delete";

  // Check if "quizzes" has changed
  if (toUpdate.quizzes) {
    // Append the information of what to add
    const { added, removed } = addRemoveArrayQuery(
      (selectedDataPlain as typeof dataToProvide).quizzes || [],
      toUpdate.quizzes,
    );

    // Adding the contacts to each list
    toUpdate.added = { quizzes: added };
    toUpdate.removed = { quizzes: removed };
  }

  return toUpdate;
};
